import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import './WorkspaceTreeNav.css';
import AddProjectModal from '../Projects/AddProjectModal';
import AddJobModal from '../Jobs/AddJobModal';
import TaskModal from '../Modals/TaskModal';

const WorkspaceTreeNav = ({
  userId,
  onFilterByTask,
  toggleStackExpansion,
  handleFilterByProject,
  handleFilterByWorkspace,
  handleFilterByJob,
  onFilterByUser,
  workspaceRole
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Search & users state
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const searchInputRef = useRef(null);
  const userDropdownRef = useRef(null);

  // Selected user for tree filtering
  const [selectedUserId, setSelectedUserId] = useState(userId);
  const [selectedUserName, setSelectedUserName] = useState('');

  // Add Project Modal state
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [selectedWorkspaceForProject, setSelectedWorkspaceForProject] = useState({ id: null, name: null });

  // Add Job Modal state
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [selectedProjectForJob, setSelectedProjectForJob] = useState({ id: null, name: null, workspaceId: null });

  // Add Task Modal state
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [selectedJobForTask, setSelectedJobForTask] = useState({ id: null, name: null });

  // Store projects, jobs, and tasks data
  const [projectsData, setProjectsData] = useState({});
  const [jobsData, setJobsData] = useState({});
  const [tasksData, setTasksData] = useState({});

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  const getLastActivityTimestamp = (item) => {
    const activityValue =
      item.lastActivity ??
      item.lastActivityDate ??
      item.activityDate ??
      item.updatedAt ??
      item.updatedDate ??
      item.modifiedAt ??
      item.modifiedDate ??
      item.createdAt ??
      item.createdDate;
    if (!activityValue) return 0;
    const parsed = new Date(activityValue).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sortByLastActivityDesc = (items, idKey) =>
    [...items].sort((a, b) => {
      const activityDiff = getLastActivityTimestamp(b) - getLastActivityTimestamp(a);
      if (activityDiff !== 0) return activityDiff;
      return (Number(b[idKey]) || 0) - (Number(a[idKey]) || 0);
    });

  const fetchNavigationTree = async (targetUserId = null) => {
    const effectiveUserId = targetUserId !== null ? targetUserId : selectedUserId;
    if (!effectiveUserId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/Tree/GetNavigationTree?userId=${effectiveUserId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.message === "Navigation tree retrieved successfully" && data.data && data.data.workspaces) {
        const workspacesList = data.data.workspaces.map(workspace => ({
          id: workspace.workspaceId,
          name: workspace.workspaceName,
          projectCount: workspace.projectCount || 0
        }));
        setWorkspaces(workspacesList);
        const projectsMap = {};
        const jobsMap = {};
        const tasksMap = {};
        data.data.workspaces.forEach(workspace => {
          if (workspace.projects) {
            projectsMap[workspace.workspaceId] = workspace.projects.map(project => ({
              id: project.projectId,
              name: project.projectName,
              workspaceId: workspace.workspaceId,
              jobCount: project.jobCount || 0
            }));
            workspace.projects.forEach(project => {
              if (project.jobs) {
                jobsMap[project.projectId] = sortByLastActivityDesc(
                  project.jobs.map(job => ({
                    id: job.jobId,
                    name: job.jobName,
                    projectId: project.projectId,
                    taskCount: job.taskCount || 0,
                  })),
                  "id"
                );
                project.jobs.forEach(job => {
                  if (job.tasks) {
                    tasksMap[job.jobId] = sortByLastActivityDesc(
                      job.tasks.map(task => ({
                        id: task.taskId,
                        name: task.taskTitle,
                        jobId: job.jobId,
                        friendlyId: task.friendlyId,
                        noteCount: task.noteCount || 0,
                        startDate: task.startDate,
                      })),
                      "id"
                    );
                  }
                });
              }
            });
          }
        });
        setProjectsData(projectsMap);
        setJobsData(jobsMap);
        setTasksData(tasksMap);
      } else {
        setWorkspaces([]);
        setProjectsData({});
        setJobsData({});
        setTasksData({});
        setError("No workspaces found");
      }
    } catch (err) {
      console.error("Error fetching navigation tree:", err);
      setError("Failed to load navigation tree");
      setWorkspaces([]);
      setProjectsData({});
      setJobsData({});
      setTasksData({});
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueUsernames = async () => {
    if (users.length > 0) return;
    setLoadingUsers(true);
    try {
      const response = await fetch(`${apiUrl}/SiteNote/GetUniqueUsernames?userId=${userId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const usernamesArray = data.usernames || [];
      const formattedUsers = usernamesArray.map(item => ({
        id: item.id,
        name: item.text,
      }));
      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
    } catch (err) {
      console.error("Error fetching usernames:", err);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const lowercasedTerm = searchTerm.toLowerCase();
      setFilteredUsers(users.filter(user => user.name.toLowerCase().includes(lowercasedTerm)));
    }
  }, [searchTerm, users]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleWorkspaceClick = (workspaceId) => {
    if (expandedWorkspace === workspaceId) {
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      setExpandedWorkspace(workspaceId);
      setExpandedProject(null);
      setExpandedJob(null);
    }
  };

  const handleWorkspaceDoubleClick = (e, workspace) => {
    e.stopPropagation();
    if (workspace.id && handleFilterByWorkspace) {
      handleFilterByWorkspace(workspace.id, workspace.name);
    }
  };

  const handleProjectClick = (projectId) => {
    if (expandedProject === projectId) {
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      setExpandedProject(projectId);
      setExpandedJob(null);
    }
  };

  const handleProjectDoubleClick = (e, project) => {
    e.stopPropagation();
    if (project.id && handleFilterByProject) {
      handleFilterByProject(project.id, project.name);
    }
  };

  const handleJobClick = (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
    }
  };

  const handleJobDoubleClick = (e, job) => {
    e.stopPropagation();
    if (job.id && handleFilterByJob) {
      handleFilterByJob(job.id, job.name);
    }
  };

  const handleTaskClick = async (task, job, project, workspace) => {
    const completeJob = {
      jobId: job.id,
      jobName: job.name,
      workspaceId: workspace?.id,
      workspaceName: workspace?.name,
      projectId: project?.id,
      projectName: project?.name,
    };
    if (onFilterByTask) {
      onFilterByTask(task.id, task.name, completeJob);
    }
    setIsOpen(false);
    setExpandedWorkspace(null);
    setExpandedProject(null);
    setExpandedJob(null);
  };

  const handleUserSelect = (user) => {
    setSelectedUserId(user.id);
    setSelectedUserName(user.name);
    setSearchTerm(user.name);
    setShowUserDropdown(false);
    fetchNavigationTree(user.id);
    setExpandedWorkspace(null);
    setExpandedProject(null);
    setExpandedJob(null);
    if (onFilterByUser) {
      onFilterByUser(user.name);
    }
  };

  const clearUserSelection = () => {
    setSelectedUserId(userId);
    setSelectedUserName('');
    setSearchTerm('');
    fetchNavigationTree(userId);
    setExpandedWorkspace(null);
    setExpandedProject(null);
    setExpandedJob(null);
    if (onFilterByUser) {
      onFilterByUser(null);
    }
  };

  const handleSearchFocus = async () => {
    setShowUserDropdown(true);
    await fetchUniqueUsernames();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setExpandedWorkspace(null);
        setExpandedProject(null);
        setExpandedJob(null);
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
      setShowUserDropdown(false);
      fetchNavigationTree();
    }
  };

  const handleAddWorkspace = (workspace) => {
    setIsOpen(false);
    setSelectedWorkspaceForProject({ id: workspace.id, name: workspace.name });
    setShowAddProjectModal(true);
  };

  const handleAddProject = (project, workspaceId, workspaceName) => {
    setIsOpen(false);
    setSelectedProjectForJob({
      id: project.id,
      name: project.name,
      workspaceId: workspaceId,
    });
    setShowAddJobModal(true);
  };

  const handleAddJob = (job, projectId, projectName) => {
    setIsOpen(false);
    setSelectedJobForTask({
      id: job.id,
      name: job.name,
    });
    setShowAddTaskModal(true);
  };

  const handleProjectAdded = async () => {
    await fetchNavigationTree(selectedUserId);
  };

  const handleJobAdded = async () => {
    await fetchNavigationTree(selectedUserId);
  };

  const handleTaskAdded = async () => {
    await fetchNavigationTree(selectedUserId);
  };

  const fetchTasksForJob = () => {};
  const refreshNotes = () => {};

  // ========== PORTAL WRAPPER WITH HIGH Z-INDEX ==========
  const renderModalWithPortal = (ModalComponent, props, isOpen) => {
    if (!isOpen) return null;
    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999999,
          pointerEvents: 'none', // allows clicks to pass through the overlay
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <ModalComponent {...props} />
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="workspace-tree-nav" ref={dropdownRef}>
      <button
        className="workspace-toggle-btn"
        onClick={toggleDropdown}
        title="Workspace Navigation"
      >
        <i className="fas fa-layer-group" />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} toggle-icon`} />
      </button>

      {isOpen && (
        <div className="workspace-dropdown slide-in-left">
          <div className="workspace-header">
            <i className="fas fa-sitemap" />
            <span>Workspace Navigation</span>
          </div>

          {/* Conditionally render user search box only for role 1 */}
          {workspaceRole === 1 && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #e9ecef', position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={handleSearchFocus}
                  style={{
                    width: '100%',
                    padding: '6px 30px 6px 30px',
                    border: '1px solid #ddd',
                    borderRadius: '20px',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
                <i className="fas fa-search" style={{ position: 'absolute', left: '10px', top: '8px', fontSize: '12px', color: '#aaa' }} />
                {searchTerm && (
                  <i
                    className="fas fa-times-circle"
                    style={{ position: 'absolute', right: '10px', top: '8px', fontSize: '12px', color: '#aaa', cursor: 'pointer' }}
                    onClick={() => clearUserSelection()}
                  />
                )}
              </div>

              {/* User dropdown */}
              {showUserDropdown && (
                <div
                  ref={userDropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 9999,
                    marginTop: '4px',
                  }}
                >
                  {loadingUsers ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                      <i className="fas fa-spinner fa-spin" /> Loading...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleUserSelect(user)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fas fa-user-circle" style={{ color: '#3498db' }} />
                        <span>{user.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          <div className="workspace-tree-content">
            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading workspaces...</span>
              </div>
            ) : error ? (
              <div className="error-state">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
                <button onClick={() => fetchNavigationTree()} className="retry-btn">
                  <i className="fas fa-redo" />
                  Retry
                </button>
              </div>
            ) : workspaces.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building" />
                <span>No workspaces found</span>
              </div>
            ) : (
              workspaces.map((workspace) => {
                const workspaceProjects = projectsData[workspace.id] || [];
                return (
                  <div key={workspace.id} className="workspace-node">
                    <div
                      className="workspace-item"
                      onClick={() => handleWorkspaceClick(workspace.id)}
                      onDoubleClick={(e) => handleWorkspaceDoubleClick(e, workspace)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className={`fas fa-chevron-${expandedWorkspace === workspace.id ? 'down' : 'right'} expand-icon`} />
                        <i className="fas fa-building workspace-icon" />
                        <div className="workspace-info">
                          <div className="workspace-name">{workspace.name}</div>
                        </div>
                        <span className="project-count">
                          ({workspace.projectCount || workspaceProjects.length})
                        </span>
                      </div>
                      <i
                        className="fas fa-plus plus-icon"
                        title="Add Project"
                        style={{ cursor: 'pointer', color: '#10b981', fontSize: '12px', marginRight: '8px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddWorkspace(workspace);
                        }}
                      />
                    </div>

                    {expandedWorkspace === workspace.id && (
                      <div className="projects-container">
                        {workspaceProjects.length === 0 ? (
                          <div className="no-projects-message">
                            <i className="fas fa-project-diagram" />
                            <span>No projects available</span>
                          </div>
                        ) : (
                          workspaceProjects.map((project) => {
                            const projectJobs = jobsData[project.id] || [];
                            return (
                              <div key={project.id} className="project-node">
                                <div
                                  className="project-item"
                                  onClick={() => handleProjectClick(project.id)}
                                  onDoubleClick={(e) => handleProjectDoubleClick(e, project)}
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <i className={`fas fa-chevron-${expandedProject === project.id ? 'down' : 'right'} expand-icon`} />
                                    <i className="fas fa-project-diagram project-icon" />
                                    <span className="project-name">{project.name}</span>
                                    <span className="job-count">
                                      ({project.jobCount || projectJobs.length})
                                    </span>
                                  </div>
                                  <i
                                    className="fas fa-plus plus-icon"
                                    title="Add Job"
                                    style={{ cursor: 'pointer', color: '#10b981', fontSize: '12px', marginRight: '8px' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddProject(project, workspace.id, workspace.name);
                                    }}
                                  />
                                </div>

                                {expandedProject === project.id && (
                                  <div className="jobs-container">
                                    {projectJobs.length === 0 ? (
                                      <div className="no-jobs-message">
                                        <i className="fas fa-tasks" />
                                        <span>No jobs available</span>
                                      </div>
                                    ) : (
                                      projectJobs.map((job) => {
                                        const jobTasks = tasksData[job.id] || [];
                                        const hasTasks = jobTasks.length > 0;
                                        return (
                                          <div key={job.id} className="job-node">
                                            <div
                                              className={`job-item ${!hasTasks ? 'no-expand' : ''}`}
                                              onClick={hasTasks ? () => handleJobClick(job.id) : undefined}
                                              onDoubleClick={(e) => handleJobDoubleClick(e, job)}
                                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {hasTasks && (
                                                  <i className={`fas fa-chevron-${expandedJob === job.id ? 'down' : 'right'} expand-icon`} />
                                                )}
                                                <i className="fas fa-tasks job-icon" />
                                                <span className="job-name">{job.name}</span>
                                                <span className="task-count">
                                                  ({job.taskCount || jobTasks.length})
                                                </span>
                                              </div>
                                              <i
                                                className="fas fa-plus plus-icon"
                                                title="Add Task"
                                                style={{ cursor: 'pointer', color: '#10b981', fontSize: '12px', marginRight: '8px' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAddJob(job, project.id, project.name);
                                                }}
                                              />
                                            </div>

                                            {expandedJob === job.id && jobTasks.length > 0 && (
                                              <div className="workspace-tasks-container">
                                                {jobTasks.map((task) => (
                                                  <div
                                                    key={task.id}
                                                    className="task-item"
                                                    onClick={() => handleTaskClick(task, job, project, workspace)}
                                                  >
                                                    <i className="fas fa-check-square task-icon" />
                                                    <span className="task-name">
                                                      {task.friendlyId ? `${task.friendlyId}: ${task.name}` : task.name}
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ========== MODAL PORTALS WITH HIGH Z-INDEX OVERLAY ========== */}
      {renderModalWithPortal(
        AddProjectModal,
        {
          isOpen: showAddProjectModal,
          onClose: () => setShowAddProjectModal(false),
          workspaceId: selectedWorkspaceForProject.id,
          workspaceName: selectedWorkspaceForProject.name,
          userId: userId,
          existingProjects: projectsData[selectedWorkspaceForProject.id] || [],
          onAddSuccess: handleProjectAdded,
        },
        showAddProjectModal
      )}

      {renderModalWithPortal(
        AddJobModal,
        {
          isOpen: showAddJobModal,
          onClose: () => setShowAddJobModal(false),
          defWorkId: selectedProjectForJob.workspaceId,
          user: userId,
          onJobAdded: handleJobAdded,
          initialProjectId: selectedProjectForJob.id,
          initialProjectName: selectedProjectForJob.name,
        },
        showAddJobModal
      )}

      {renderModalWithPortal(
        TaskModal,
        {
          isOpen: showAddTaskModal,
          onClose: () => setShowAddTaskModal(false),
          jobId: selectedJobForTask.id,
          jobName: selectedJobForTask.name,
          onSaveTask: handleTaskAdded,
          fetchTasksForJob: fetchTasksForJob,
          refreshNotes: refreshNotes,
          isLoading: false,
        },
        showAddTaskModal
      )}
    </div>
  );
};

export default WorkspaceTreeNav;